import React from "react";
import { isValid, parseISO } from "date-fns";
import { FormikErrors, FormikTouched } from "formik";
import { makeStyles } from "@material-ui/core";
import { ConfirmationDialogParams } from "../../../../components/Dialog/ConfirmationDialog/ConfirmationDialog";
import InfoBox from "../../../../components/InfoBox";
import CustomDatePickerTimeZoneSave from "../../../../components/Input/CustomDatePickerTimeZoneSave"; // Updated import
import isDateAfter from "../../../../functions/isDateAfter.function";

const INFO_TEXT = `Use this input field to adjust the estimated finishing date
 for the completion of the test request at any time during the planning and
 execution processes.`;

interface EstimatedFinishingDateInputProps<
  T extends { estimatedFinishingDate: string | null; deadline: string | null },
> {
  estimatedFinishingDate: string | null;
  disabled: boolean;
  disablePast: boolean;
  handleDirtyChange: () => void;
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
  showConfirmationDialog: (params: ConfirmationDialogParams) => Promise<string>;
  values: T;
  errors?: FormikErrors<T>;
  touched?: FormikTouched<T>;
}

const errorColor = "rgb(191, 8, 20)";

const useStyles = makeStyles({
  error: {
    "& .MuiInputBase-root": {
      borderColor: errorColor,
      "&:hover": {
        borderColor: errorColor,
      },
    },
    // Colors the calendar SVG icon when the input is in error state
    "& .MuiInputBase-root .MuiSvgIcon-root": {
      color: errorColor,
    },
  },
  helperText: {
    position: "relative",
    color: errorColor,
    fontSize: "0.75rem",
    left: "14px",
  },
});

function EstimatedFinishingDateInput<T extends { estimatedFinishingDate: string | null; deadline: string | null }>(
  props: EstimatedFinishingDateInputProps<T>,
) {
  const {
    estimatedFinishingDate,
    disabled,
    disablePast,
    handleDirtyChange,
    setFieldValue,
    showConfirmationDialog,
    values,
    errors,
    touched,
  } = props;

  const classes = useStyles();

  const handleChange = (newDate: string | null) => {
    // Convert string to a Date for validation checks
    const parsedDate = newDate ? parseISO(newDate) : null;
    const parsedDeadline = values.deadline ? parseISO(values.deadline) : null;

    if (isValid(parsedDate) && isDateAfter(parsedDate, isValid(parsedDeadline) ? parsedDeadline : null)) {
      showConfirmationDialog({
        text: (
          <>
            The selected estimated finishing date is later than the deadline set by the requester. Please contact the
            requester.
          </>
        ),
      })
        .then(() => {
          handleDirtyChange();
          setFieldValue("estimatedFinishingDate", newDate);
        })
        .catch(() => {
          setFieldValue("estimatedFinishingDate", estimatedFinishingDate);
        });
    } else {
      handleDirtyChange();
      setFieldValue("estimatedFinishingDate", newDate);
    }
  };

  const hasError = touched?.estimatedFinishingDate && Boolean(errors?.estimatedFinishingDate);

  return (
    <InfoBox text={INFO_TEXT}>
      <div className={hasError ? classes.error : ""}>
        <CustomDatePickerTimeZoneSave
          disablePast={disablePast}
          disabled={disabled}
          label="Estimated Finishing Date (EFD)*"
          value={values.estimatedFinishingDate} // Just pass the string "YYYY-MM-DD"
          onChange={handleChange}
          error={hasError}
        />
      </div>
      {hasError && <div className={classes.helperText}>{errors?.estimatedFinishingDate}</div>}
    </InfoBox>
  );
}

export default EstimatedFinishingDateInput;
